import { useMemo } from 'react';

import { DateTimeFormat } from '../utils/Format';
import useTimeMode from './useTimeMode';

/**
 * `DateTimeFormat` bound to the app-wide Local/UTC preference.
 *
 * Use this instead of calling `DateTimeFormat` directly whenever a component
 * renders a timestamp that came from the API, so the value follows the toggle
 * beside the breadcrumbs. Table cells read the mode from the table instance
 * instead -- see `utils/columns/timeModeCell`.
 */
export default function useDateTimeFormat() {
  const { timeMode, isUtc } = useTimeMode();

  return useMemo(
    () => ({
      timeMode,
      isUtc,
      // 'UTC' or 'Local', and a label tagged with it: `withTimeMode('Last Seen')` ->
      // 'Last Seen (UTC)'. Screens outside a table have no toggle in view, so the label
      // is the only thing telling the reader which clock a timestamp is on.
      timeModeName: isUtc ? 'UTC' : 'Local',
      withTimeMode: (label: string) => `${label} (${isUtc ? 'UTC' : 'Local'})`,
      toStandardDate: (date: any, withoutTime = false) => DateTimeFormat.toStandardDate(date, isUtc, withoutTime),
      toStandardDateWithSeconds: (date: any, withoutTime = false) => DateTimeFormat.toStandardDateWithSeconds(date, isUtc, withoutTime),
      toTimeFromNow: (date: any) => DateTimeFormat.toTimeFromNow(date, isUtc),
    }),
    [timeMode, isUtc],
  );
}
